/**
 * SalesmenReport — Salesman Performance & Recovery comparison.
 * Owner can compare all salesmen + click into a drill-down dashboard.
 */
import React, { useCallback, useEffect, useState } from "react";
import { api, fmtPKR } from "../lib/api";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { Users, TrendingUp, Wallet, AlertTriangle, ReceiptText, Calendar, X } from "lucide-react";

function Tile({ icon: Icon, label, value, hint, tone = "slate", testid }) {
  const tones = {
    slate: "bg-slate-50 border-slate-200",
    emerald: "bg-emerald-50 border-emerald-200",
    amber: "bg-amber-50 border-amber-200",
    red: "bg-red-50 border-red-200",
    sky: "bg-sky-50 border-sky-200",
  };
  return (
    <div className={`rounded-md border p-4 ${tones[tone]}`} data-testid={testid}>
      <div className="flex items-center gap-2 mb-2">
        <Icon className="w-4 h-4 text-slate-500" />
        <div className="text-xs uppercase tracking-wider font-bold text-slate-500">{label}</div>
      </div>
      <div className="text-xl font-display font-bold">{value}</div>
      {hint && <div className="text-xs text-slate-500 mt-1">{hint}</div>}
    </div>
  );
}

export default function SalesmenReport() {
  const [rows, setRows] = useState([]);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [selected, setSelected] = useState(null);  // salesman_id for drill-down
  const [detail, setDetail] = useState(null);

  const load = useCallback(() => {
    const params = {};
    if (dateFrom) params.date_from = dateFrom;
    if (dateTo) params.date_to = dateTo;
    api.get("/salesmen/stats", { params }).then((r) => setRows(r.data || []));
  }, [dateFrom, dateTo]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!selected) { setDetail(null); return; }
    api.get(`/salesmen/${selected}/dashboard`).then((r) => setDetail(r.data));
  }, [selected]);

  return (
    <div className="space-y-6" data-testid="salesmen-report">
      <div className="flex items-baseline justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-display font-bold">Salesman Performance</h1>
          <div className="text-sm text-slate-500">Compare salesmen — loans created, recovery, outstanding & overdue.</div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white border border-slate-200 rounded-md p-4 flex flex-wrap gap-3 items-end">
        <Calendar className="w-4 h-4 text-slate-400 mb-2" />
        <div className="space-y-1">
          <Label>From (loan start date)</Label>
          <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="rep-from" />
        </div>
        <div className="space-y-1">
          <Label>To</Label>
          <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="rep-to" />
        </div>
        {(dateFrom || dateTo) && (
          <Button variant="outline" onClick={() => { setDateFrom(""); setDateTo(""); }}>
            <X className="w-3.5 h-3.5 mr-1" /> Clear
          </Button>
        )}
      </div>

      {/* Comparison table */}
      <div className="bg-white border border-slate-200 rounded-md overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
            <tr>
              <th className="px-4 py-3 text-left">Salesman</th>
              <th className="px-4 py-3 text-right">Customers</th>
              <th className="px-4 py-3 text-right">Loans</th>
              <th className="px-4 py-3 text-right">Loan Amount</th>
              <th className="px-4 py-3 text-right">Recovery</th>
              <th className="px-4 py-3 text-right">Outstanding</th>
              <th className="px-4 py-3 text-right">Overdue</th>
              <th className="px-4 py-3 text-right">Recovery %</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows.map((r) => {
              const tone = r.recovery_rate_pct >= 85 ? "text-emerald-700" : r.recovery_rate_pct >= 60 ? "text-amber-600" : "text-red-600";
              return (
                <tr key={r.salesman_id || "__unassigned__"}
                    className="hover:bg-slate-50 cursor-pointer"
                    onClick={() => r.salesman_id && setSelected(r.salesman_id)}
                    data-testid={`sm-row-${r.salesman_id || 'unassigned'}`}>
                  <td className="px-4 py-3">
                    <div className="font-semibold">{r.salesman_name}</div>
                    <div className="text-xs text-slate-500">{r.role}</div>
                  </td>
                  <td className="px-4 py-3 text-right tabular">{r.customers}</td>
                  <td className="px-4 py-3 text-right tabular">{r.loans_count}</td>
                  <td className="px-4 py-3 text-right tabular">{fmtPKR(r.loan_amount)}</td>
                  <td className="px-4 py-3 text-right tabular text-emerald-700">{fmtPKR(r.recovery_amount)}</td>
                  <td className="px-4 py-3 text-right tabular">{fmtPKR(r.outstanding)}</td>
                  <td className="px-4 py-3 text-right tabular text-red-600">{r.overdue_count}</td>
                  <td className={`px-4 py-3 text-right tabular font-bold ${tone}`}>{r.recovery_rate_pct}%</td>
                </tr>
              );
            })}
            {rows.length === 0 && (
              <tr><td colSpan="8" className="px-4 py-8 text-center text-slate-500">No loans found in selected range.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Drill-down */}
      {selected && detail && (
        <div className="bg-white border border-slate-200 rounded-md p-5 space-y-4" data-testid="sm-detail">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-xs uppercase tracking-wider font-bold text-slate-500">Salesman Dashboard</div>
              <div className="text-xl font-display font-bold">{detail.salesman.name}</div>
              <div className="text-xs text-slate-500">{detail.salesman.email} · {detail.salesman.role}</div>
            </div>
            <Button variant="outline" size="sm" onClick={() => setSelected(null)}><X className="w-3.5 h-3.5 mr-1" /> Close</Button>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Tile icon={Users} label="Customers" value={detail.total_customers} testid="sm-d-customers" />
            <Tile icon={ReceiptText} label="Loans" value={detail.total_loans} testid="sm-d-loans" />
            <Tile icon={Wallet} label="Loan Value" value={fmtPKR(detail.total_loan_amount)} testid="sm-d-loan-amt" />
            <Tile icon={TrendingUp} label="Recovery Today" value={fmtPKR(detail.recovery_today)} tone="emerald" testid="sm-d-today" />
            <Tile icon={TrendingUp} label="Recovery This Month" value={fmtPKR(detail.recovery_month)} tone="emerald" testid="sm-d-month" />
            <Tile icon={TrendingUp} label="Total Recovery" value={fmtPKR(detail.recovery_total)} tone="emerald" testid="sm-d-total" />
            <Tile icon={Wallet} label="Outstanding" value={fmtPKR(detail.outstanding)} tone="amber" testid="sm-d-outstanding" />
            <Tile icon={AlertTriangle} label="Overdue Customers" value={detail.overdue_customers} tone="red" testid="sm-d-overdue" />
          </div>
          <div className="text-xs text-slate-500">
            Recovery rate: <span className="font-bold">{detail.recovery_rate_pct}%</span> (recovery ÷ loan value).
          </div>
        </div>
      )}
    </div>
  );
}
